/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { cn } from "../../lib/utils";

interface ToggleOption {
  value: string;
  label: string;
}

interface ToggleButtonProps {
  options: ToggleOption[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  ariaLabel?: string;
  disabled?: boolean;
}

export function ToggleButton({
  options,
  value,
  onChange,
  className,
  ariaLabel,
  disabled = false,
}: ToggleButtonProps) {
  const selectedIndex = options.findIndex((o) => o.value === value);
  const count = options.length;

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cn(
        "relative grid h-11 rounded-xl border border-border bg-bg-base p-0.5",
        disabled && "opacity-60",
        className,
      )}
      style={{ gridTemplateColumns: `repeat(${count}, 1fr)` }}
    >
      {selectedIndex >= 0 && (
        <div
          className="pointer-events-none absolute inset-y-0.5 rounded-xl border border-border-hover bg-bg-elevated shadow-light transition-all duration-200 ease-out"
          style={{
            left: `calc(${(selectedIndex / count) * 100}% + 2px)`,
            width: `calc(${(1 / count) * 100}% - 4px)`,
          }}
        />
      )}

      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={value === option.value}
          disabled={disabled}
          onClick={() => onChange(option.value)}
          className={cn(
            "relative z-10 cursor-pointer whitespace-nowrap disabled:cursor-not-allowed rounded-xl px-4 py-1.5 text-sm transition-colors duration-200 focus:outline-none",
            value === option.value
              ? "font-medium text-text-primary"
              : "text-text-tertiary hover:text-text-secondary",
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
